import { useEffect, useMemo, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { Sparkles, Loader2 } from 'lucide-react'
import type { Category } from '../../types/@types/category'
import Modal from '../common/Modal'
import { cn, inputClass, labelClass } from '../../utils/theme'
import { useCreateProduct, useProduct, useUpdateProduct } from '../../hooks/useProducts'
import type { ProductInput, ProductAdditionsBlock, ProductExceptionsBlock } from '../../types/product'
import ProductOptionsEditor from './ProductOptionsEditor'
import { getImageUrl } from '../../utils/url'
import { generateProductDescription } from '../../services/AIService'

interface ProductFormModalProps {
  mode: 'create' | 'edit'
  isOpen: boolean
  onClose: () => void
  productId?: string | number | null
  categories: Category[]
}

interface FormValues {
  name: string
  description: string
  price: string
  takeawayPrice: string
  categoryId: string
  active: 'true' | 'false'
  ingredientsText: string
  maxFlavors: string
  preparationArea: string
  image?: FileList | null
}

type FormContext = { mode: 'create' | 'edit' }

const schema: yup.Schema<FormValues, FormContext> = yup.object({
  name: yup.string().trim().required('El nombre es obligatorio'),
  description: yup.string().trim().required('La descripción es obligatoria'),
  price: yup
    .string()
    .required('El precio es obligatorio')
    .test('is-number', 'Ingresa un precio válido', (value) => {
      const numberValue = Number(value?.replace(/,/g, '.'))
      return !Number.isNaN(numberValue) && numberValue >= 0
    }),
  takeawayPrice: yup
    .string()
    .transform((value) => value ?? '0')
    .test('is-number', 'Ingresa un valor válido', (value) => {
      if (!value) return true
      const parsed = Number(value.replace(/,/g, '.'))
      return !Number.isNaN(parsed) && parsed >= 0
    })
    .default('0'),
  categoryId: yup.string().trim().required('Selecciona una categoría'),
  active: yup.mixed<'true' | 'false'>().oneOf(['true', 'false']).default('true'),
  ingredientsText: yup
    .string()
    .transform((value) => value ?? '')
    .test('has-ingredients', 'Agrega al menos un ingrediente', (value) => {
      if (!value) return false
      const ingredients = value
        .split(/\r?\n|,/)
        .map((item) => item.trim())
        .filter(Boolean)
      return ingredients.length > 0
    })
    .required('Agrega al menos un ingrediente'),
  maxFlavors: yup
    .string()
    .transform((value) => value ?? '')
    .test('is-number', 'Ingresa un número entero válido', (value) => {
      if (!value) return true
      const parsed = Number(value)
      return Number.isInteger(parsed) && parsed >= 0
    })
    .default('0'),
  preparationArea: yup.string().default('1'),
  image: yup
    .mixed<FileList>()
    .test('file-required', 'La imagen es obligatoria', function (value) {
      const { mode } = this.options.context as FormContext
      if (mode === 'create') {
        return Boolean(value && value.length > 0)
      }
      return true
    })
    .test('file-size', 'La imagen debe pesar menos de 4MB', (value) => {
      if (!value || value.length === 0) return true
      return value[0].size <= 64 * 1024 * 1024
    })
    .test('file-type', 'Solo se permiten imágenes', (value) => {
      if (!value || value.length === 0) return true
      return value[0].type.startsWith('image/')
    })
    .default(undefined),
})

function getDefaultValues(): FormValues {
  return {
    name: '',
    description: '',
    price: '',
    takeawayPrice: '0',
    categoryId: '',
    active: 'true',
    ingredientsText: '',
    maxFlavors: '0',
    preparationArea: '1',
    image: undefined,
  }
}

type ProductOptionsState = {
  additions: ProductAdditionsBlock
  exceptions: ProductExceptionsBlock
}

function createDefaultAdditions(): ProductAdditionsBlock {
  return { enabled: false, options: [] }
}

function createDefaultExceptions(): ProductExceptionsBlock {
  return { enabled: false, options: [] }
}

function createDefaultOptionsState(): ProductOptionsState {
  return {
    additions: createDefaultAdditions(),
    exceptions: createDefaultExceptions(),
  }
}

function cloneAdditions(block?: ProductAdditionsBlock): ProductAdditionsBlock {
  if (!block || !block.enabled) {
    return createDefaultAdditions()
  }
  return {
    enabled: true,
    options: (block.options ?? []).map((option) => ({
      ...option,
      priceDelta: Number(option.priceDelta ?? 0),
      maxQty: Math.max(1, Number(option.maxQty ?? 1)),
      active: Boolean(option.active),
    })),
  }
}

function cloneExceptions(block?: ProductExceptionsBlock): ProductExceptionsBlock {
  if (!block || !block.enabled) {
    return createDefaultExceptions()
  }
  return {
    enabled: true,
    options: (block.options ?? []).map((option) => ({
      ...option,
      affectsPrice: Boolean(option.affectsPrice),
      priceDelta: option.affectsPrice ? Number(option.priceDelta ?? 0) : 0,
      active: Boolean(option.active),
    })),
  }
}

function sanitizeProductOptions(options: ProductOptionsState): ProductOptionsState {
  return {
    additions: cloneAdditions(options.additions),
    exceptions: cloneExceptions(options.exceptions),
  }
}

export function ProductFormModal({
  mode,
  isOpen,
  onClose,
  productId,
  categories,
}: ProductFormModalProps) {
  const initialFocusRef = useRef<HTMLButtonElement | null>(null)
  const [generatingDesc, setGeneratingDesc] = useState(false)

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: yupResolver(schema as any),
    defaultValues: getDefaultValues(),
    context: { mode },
  })

  const { data: product, isLoading: isLoadingProduct } = useProduct(
    isOpen && mode === 'edit' ? productId ?? undefined : undefined,
  )

  const createMutation = useCreateProduct()
  const updateMutation = useUpdateProduct()
  const [options, setOptions] = useState<ProductOptionsState>(createDefaultOptionsState)

  const handleGenerateDescription = async () => {
    const name = watch('name')?.trim()
    if (!name) return
    const categoryId = watch('categoryId')
    const categoryName = categories.find(c => String(c.id) === String(categoryId))?.name
    setGeneratingDesc(true)
    try {
      const desc = await generateProductDescription(name, categoryName)
      setValue('description', desc, { shouldValidate: true })
    } catch {
      // silently ignore — no molesta al usuario si la IA falla
    } finally {
      setGeneratingDesc(false)
    }
  }

  useEffect(() => {
    if (!isOpen) {
      reset(getDefaultValues())
      setOptions(createDefaultOptionsState())
      return
    }

    if (mode === 'edit' && product) {
      reset({
        name: product.name,
        description: product.description,
        price: String(product.price),
        takeawayPrice: String(product.takeawayPrice ?? 0),
        categoryId: String(product.category?.id ?? ''),
        active: product.active ? 'true' : 'false',
        ingredientsText: product.ingredients.map((ingredient) => ingredient.name).join(', '),
        maxFlavors: String(product.maxFlavors ?? 0),
        preparationArea: String(product.preparationArea ?? 1),
        image: undefined,
      })
      setOptions({
        additions: cloneAdditions(product.additions),
        exceptions: cloneExceptions(product.exceptions),
      })
    } else if (mode === 'create') {
      reset(getDefaultValues())
      setOptions(createDefaultOptionsState())
    }
  }, [isOpen, mode, product, reset])

  const parsedCategories = useMemo(
    () =>
      categories.map((category) => ({
        id: (category as any).id ?? category.id ?? category.name,
        name: category.name,
      })),
    [categories],
  )

  const buildIngredientsArray = (text: string) =>
    text
      .split(/\r?\n|,/)
      .map((item) => item.trim())
      .filter(Boolean)

  const onSubmit = handleSubmit(async (values) => {
    const ingredients = buildIngredientsArray(values.ingredientsText)
    const sanitizedOptions = sanitizeProductOptions(options)
    const payload: Omit<ProductInput, 'ingredients'> & { ingredients: string[] } = {
      name: values.name.trim(),
      description: values.description.trim(),
      price: Number(values.price.replace(/,/g, '.')),
      takeawayPrice: Number(values.takeawayPrice.replace(/,/g, '.')),
      categoryId: values.categoryId,
      active: values.active === 'true',
      ingredients,
      maxFlavors: Number(values.maxFlavors || '0') || 0,
      preparationArea: Number(values.preparationArea || '1') || 1,
      additions: sanitizedOptions.additions,
      exceptions: sanitizedOptions.exceptions,
    }

    if (mode === 'create') {
      const fd = new FormData()
      fd.append('name', payload.name)
      fd.append('description', payload.description)
      fd.append('price', String(payload.price))
      fd.append('takeawayPrice', String(payload.takeawayPrice ?? 0))
      fd.append('category', String(payload.categoryId ?? ''))
      fd.append('active', String(payload.active))
      fd.append('ingredients', JSON.stringify(ingredients))
      fd.append('maxFlavors', String(payload.maxFlavors ?? 0))
      fd.append('additions', JSON.stringify(payload.additions))
      fd.append('exceptions', JSON.stringify(payload.exceptions))
      fd.append('preparationArea', String(payload.preparationArea ?? 1))
      const imageValue = values.image
      const file =
        imageValue instanceof FileList
          ? imageValue.length > 0
            ? imageValue.item(0) ?? imageValue[0]
            : undefined
          : Array.isArray(imageValue)
            ? imageValue[0]
            : undefined

      if (file) {
        fd.append('image', file)
      }

      await createMutation.mutateAsync(fd)
      onClose()
      reset(getDefaultValues())
      setOptions(createDefaultOptionsState())
      return
    }

    if (mode === 'edit' && productId) {
      const imageValue = values.image
      const file =
        imageValue instanceof FileList
          ? imageValue.length > 0
            ? imageValue.item(0) ?? imageValue[0]
            : undefined
          : Array.isArray(imageValue)
            ? imageValue[0]
            : undefined

      if (file) {
        const fd = new FormData()
        fd.append('image', file)
        fd.append('name', payload.name)
        fd.append('description', payload.description)
        fd.append('price', String(payload.price))
        fd.append('takeawayPrice', String(payload.takeawayPrice ?? 0))
        fd.append('category', String(payload.categoryId ?? ''))
        fd.append('active', String(payload.active))
        fd.append('ingredients', JSON.stringify(ingredients))
        fd.append('maxFlavors', String(payload.maxFlavors ?? 0))
        fd.append('additions', JSON.stringify(payload.additions))
        fd.append('exceptions', JSON.stringify(payload.exceptions))
        fd.append('preparationArea', String(payload.preparationArea ?? 1))

        await updateMutation.mutateAsync({ id: productId.toString(), body: fd })
      } else {
        await updateMutation.mutateAsync({ id: productId.toString(), body: payload })
      }
      onClose()
      setOptions(createDefaultOptionsState())
    }
  })

  const isMutating = createMutation.isPending || updateMutation.isPending

  // 🔥 URL de la imagen usando la utilidad centralizada
  const imageSrc = getImageUrl(product?.imageUrl)

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={mode === 'create' ? 'Agregar producto' : 'Editar producto'}
      initialFocusRef={initialFocusRef}
      size="lg"
      footer={
        <div className="flex justify-end gap-2">
          <button
            type="button"
            className={cn(
              'rounded-full px-4 py-2 text-sm font-medium',
              'text-neutral-600 hover:text-neutral-800 dark:text-neutral-300 dark:hover:text-neutral-100',
            )}
            onClick={onClose}
            disabled={isMutating}
          >
            Cancelar
          </button>
          <button
            ref={initialFocusRef}
            type="button"
            onClick={onSubmit}
            disabled={isMutating || isSubmitting}
            className={cn(
              'inline-flex items-center rounded-full bg-blue-600 px-4 py-2 text-sm font-semibold text-white transition-colors',
              'hover:bg-blue-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2',
              'disabled:cursor-not-allowed disabled:opacity-60 dark:focus-visible:ring-offset-neutral-900',
            )}
          >
            {isMutating ? 'Guardando…' : 'Guardar'}
          </button>
        </div>
      }
    >
      {mode === 'edit' && isLoadingProduct ? (
        <p className="text-sm text-neutral-500 dark:text-neutral-400">Cargando…</p>
      ) : (
        <form className="space-y-4" onSubmit={(event) => event.preventDefault()}>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-4">
            <div className="space-y-2">
              <label className={labelClass()} htmlFor="product-name">
                Nombre
              </label>
              <input
                id="product-name"
                type="text"
                {...register('name')}
                className={inputClass()}
              />
              {errors.name ? <p className="text-xs text-rose-500">{errors.name.message}</p> : null}
            </div>
            <div className="space-y-2">
              <label className={labelClass()} htmlFor="product-price">
                Precio
              </label>
              <input
                id="product-price"
                type="number"
                step="0.01"
                {...register('price')}
                className={inputClass()}
              />
              {errors.price ? <p className="text-xs text-rose-500">{errors.price.message}</p> : null}
            </div>
            <div className="space-y-2">
              <label className={labelClass()} htmlFor="product-takeaway-price">
                Precio (Llevar)
              </label>
              <input
                id="product-takeaway-price"
                type="number"
                step="0.01"
                min="0"
                {...register('takeawayPrice')}
                className={inputClass()}
              />
              {errors.takeawayPrice ? (
                <p className="text-xs text-rose-500">{errors.takeawayPrice.message}</p>
              ) : null}
            </div>
            <div className="space-y-2">
              <label className={labelClass()} htmlFor="product-max-flavors">
                Máximo de sabores
              </label>
              <input
                id="product-max-flavors"
                type="number"
                min="0"
                step="1"
                {...register('maxFlavors')}
                className={inputClass()}
              />
              {errors.maxFlavors ? (
                <p className="text-xs text-rose-500">{errors.maxFlavors.message}</p>
              ) : null}
            </div>
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className={labelClass()} htmlFor="product-description">
                Descripción
              </label>
              <button
                type="button"
                onClick={handleGenerateDescription}
                disabled={generatingDesc || !watch('name')?.trim()}
                className="flex items-center gap-1.5 rounded-lg bg-primary/10 px-2.5 py-1 text-xs font-medium text-primary transition hover:bg-primary/20 disabled:cursor-not-allowed disabled:opacity-40"
                title="Generar descripción con IA"
              >
                {generatingDesc
                  ? <><Loader2 size={12} className="animate-spin" /> Generando…</>
                  : <><Sparkles size={12} /> Generar con IA</>
                }
              </button>
            </div>
            <textarea
              id="product-description"
              rows={3}
              {...register('description')}
              className={cn(inputClass(), 'resize-y')}
            />
            {errors.description ? (
              <p className="text-xs text-rose-500">{errors.description.message}</p>
            ) : null}
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <label className={labelClass()} htmlFor="product-category">
                Categoría
              </label>
              <select id="product-category" {...register('categoryId')} className={inputClass()}>
                <option value="">Selecciona una categoría</option>
                {parsedCategories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
              {errors.categoryId ? (
                <p className="text-xs text-rose-500">{errors.categoryId.message}</p>
              ) : null}
            </div>
            <div className="space-y-2">
              <label className={labelClass()} htmlFor="product-status">
                Estado
              </label>
              <select id="product-status" {...register('active')} className={inputClass()}>
                <option value="true">Activo</option>
                <option value="false">Inactivo</option>
              </select>
            </div>
            <div className="space-y-2">
              <label className={labelClass()} htmlFor="product-preparation-area">
                Pantalla cocina
              </label>
              <select id="product-preparation-area" {...register('preparationArea')} className={inputClass()}>
                <option value="1">Cocina 1</option>
                <option value="2">Cocina 2</option>
              </select>
            </div>
          </div>
          <div className="space-y-2">
            <label className={labelClass()} htmlFor="product-ingredients">
              Ingredientes (separados por coma o salto de línea)
            </label>
            <textarea
              id="product-ingredients"
              rows={3}
              {...register('ingredientsText')}
              className={cn(inputClass(), 'resize-y')}
            />
            {errors.ingredientsText ? (
              <p className="text-xs text-rose-500">{errors.ingredientsText.message}</p>
            ) : null}
          </div>
          <ProductOptionsEditor value={options} onChange={setOptions} />
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <label className={labelClass()} htmlFor="product-image">
                {mode === 'create' ? 'Imagen (máx. 4MB)' : 'Cambiar imagen (máx. 4MB)'}
              </label>
              <input id="product-image" type="file" accept="image/*" {...register('image')} className="w-full text-xs" />
              {errors.image ? <p className="text-xs text-rose-500">{errors.image.message as string}</p> : null}
            </div>

            {mode === 'edit' && product?.imageUrl ? (
              <div className="space-y-2 text-center sm:text-left">
                <span className={labelClass()}>Imagen actual</span>
                <img
                  src={imageSrc}
                  alt={`Imagen de ${product.name}`}
                  className="h-24 w-full rounded-md object-cover shadow-sm ring-1 ring-neutral-200 dark:ring-neutral-800"
                />
              </div>
            ) : null}
          </div>
        </form>
      )}
    </Modal>
  )
}

export default ProductFormModal
